import React, { useState, useEffect, useCallback, useRef } from "react";
import { post, STATIC_AUTH, C, LOGO_BASE } from "./utils";
import { useTheme } from "./AppShell";

const API_BASE = "https://cip.medsphere.net/api";

async function apiFetch(endpoint, body, user, activeSub) {
  return post(endpoint, {
    subprojectid: activeSub?.SUBPROJECTID || "",
    userid:       user?.USERID            || "",
    ...body,
  }, user?.AUTH_CODE || STATIC_AUTH);
}

async function apiFetchFormData(endpoint, fd, user) {
  const r = await fetch(`${API_BASE}/${endpoint}`, {
    method: "POST",
    headers: { "Auth-Code": user?.AUTH_CODE || STATIC_AUTH },
    body: fd,
  });
  return r.json();
}

// extract newly created ID from any add API response shape
function extractNewId(res, idKey) {
  const data = res.DATA;
  if (!data) return null;
  if (Array.isArray(data)) return data[0]?.[idKey] ?? null;
  const first = Object.values(data).find(v => Array.isArray(v) && v.length > 0);
  return first?.[0]?.[idKey] ?? null;
}

const SINGULAR = {
  brand:"Product", company:"Company", indication:"Indication",
  phase:"Phase", tstatus:"Trial Status", country:"Country",
  region:"Region", moa:"MoA", generic:"Generic",
  class:"Class", subclass:"Sub Class", roa:"RoA",
};

// editKey = the key in the list API response that holds the ID for this drop field
const FORM_CFG = {
  brand: {
    addEndpoint:"add_brand", dropsEndpoint:"get_brand_drops",
    editIdParam:"brandid", editIdKey:"BRANDID",
    fields: [
      { name:"brand",             label:"Product Name",    type:"text",      required:true,  editKey:"BRANDNAME"       },
      { name:"brand_abbrevation", label:"Abbreviation",    type:"text",      required:false, editKey:"BRAND_ABBREVATION"},
      { name:"moaid",             label:"MoA",             type:"drop",      dropKey:"MOA",      idKey:"MOAID",      labelKey:"MOANAME",  editKey:"MOAID",
        quickAdd:{ endpoint:"add_moa",      paramName:"moaname",  label:"MoA"       } },
      { name:"genericid",         label:"Generic",         type:"drop",      dropKey:"GENERIC",  idKey:"GENERICID",  labelKey:"GENERIC",  editKey:"GENERICID",
        quickAdd:{ endpoint:"add_generic",  paramName:"generic",  label:"Generic"   } },
      { name:"subclassid",        label:"Sub Class",       type:"drop",      dropKey:"SUBCLASS", idKey:"SUBCLASSID", labelKey:"SUBCLASS", editKey:"SUBCLASSID",
        quickAdd:{ endpoint:"add_subclass", paramName:"subclass", label:"Sub Class",
          extraFields:[{ param:"classid", label:"Class", dropKey:"CLASS", idKey:"CLASSID", labelKey:"CLASS" }] } },
      { name:"deliveryid",        label:"Routes of Admin", type:"multiDrop", dropKey:"DELIVERY", idKey:"DELIVEYID",  labelKey:"DELIVERY", editKey:"ROA",
        quickAdd:{ endpoint:"add_roa",      paramName:"delivery", label:"Route"     } },
    ],
  },
  company: {
    addEndpoint:"add_company", editIdParam:"sponsorid", editIdKey:"SPONSORID",
    fields: [
      { name:"sponsorname",  label:"Company Name", type:"text", required:true,  editKey:"SPONSORNAME"  },
      { name:"abbrevation",  label:"Abbreviation", type:"text", required:false, editKey:"SPONSORABBREV"},
      { name:"logo",         label:"Company Logo", type:"file", required:false, accept:"image/*"       },
    ],
  },
  indication: {
    addEndpoint:"add_ind", editIdParam:"indicationid", editIdKey:"INDICATIONID",
    fields: [
      { name:"indication",  label:"Indication",   type:"text", required:true,  editKey:"INDICATIONNAME"   },
      { name:"abbrevation", label:"Abbreviation", type:"text", required:false, editKey:"INDICATIONABBREV" },
    ],
  },
  phase: {
    addEndpoint:"add_phase", editIdParam:"phaseid", editIdKey:"PHASEID",
    fields: [
      { name:"phase", label:"Phase Name",    type:"text",   required:true,  editKey:"PHASENAME" },
      { name:"sort",  label:"Sort Order",    type:"number", required:false, editKey:"SORTING"     },
      { name:"chart", label:"Show in Chart", type:"toggle", required:false, editKey:"CHART"     },
    ],
  },
  tstatus: {
    addEndpoint:"add_trialstatus", editIdParam:"trialstatusid", editIdKey:"TRIALSTATUSID",
    fields: [
      { name:"trialstatusname", label:"Status Name", type:"text", required:true, editKey:"TRIALSTATUSNAME" },
    ],
  },
  country: {
    addEndpoint:"add_country", editIdParam:"countryid", editIdKey:"COUNTRYID",
    fields: [
      { name:"country",  label:"Country Name", type:"text", required:true,  editKey:"COUNTRYNAME" },
      { name:"regionid", label:"Region",       type:"singleChip", required:false, editKey:"REGIONID",
        dropKey:"REGION", idKey:"REGIONID", labelKey:"REGIONNAME", listEndpoint:"get_region_list",
        quickAdd:{ endpoint:"add_region", paramName:"region", label:"Region" } },
    ],
  },
  region: {
    addEndpoint:"add_region", editIdParam:"regionid", editIdKey:"REGIONID",
    fields: [
      { name:"region", label:"Region Name", type:"text", required:true, editKey:"REGIONNAME" },
    ],
  },
  moa: {
    addEndpoint:"add_moa", editIdParam:"moaid", editIdKey:"MOAID",
    fields: [
      { name:"moaname",     label:"MoA Name",     type:"text", required:true,  editKey:"MOANAME"   },
      { name:"abbrevation", label:"Abbreviation", type:"text", required:false, editKey:"MOAABBREV" },
    ],
  },
  generic: {
    addEndpoint:"add_generic", editIdParam:"genericid", editIdKey:"GENERICID",
    fields: [
      { name:"generic", label:"Generic Name", type:"text", required:true, editKey:"GENERIC" },
    ],
  },
  class: {
    addEndpoint:"add_class", editIdParam:"classid", editIdKey:"CLASSID",
    fields: [
      { name:"class", label:"Class Name", type:"text", required:true, editKey:"CLASS" },
    ],
  },
  subclass: {
    addEndpoint:"add_subclass", editIdParam:"subclassid", editIdKey:"SUBCLASSID",
    fields: [
      { name:"subclass", label:"Sub Class Name", type:"text", required:true,  editKey:"SUBCLASS" },
      { name:"classid",  label:"Class",          type:"singleChip", required:false, editKey:"CLASSID",
        dropKey:"CLASS", idKey:"CLASSID", labelKey:"CLASS", listEndpoint:"get_class_list",
        quickAdd:{ endpoint:"add_class", paramName:"class", label:"Class" } },
    ],
  },
  roa: {
    addEndpoint:"add_roa", editIdParam:"deliveryid", editIdKey:"DELIVERYID",
    fields: [
      { name:"delivery", label:"RoA", type:"text", required:true, editKey:"DELIVERY" },
    ],
  },
};

// ─── Toast ────────────────────────────────────────────────────────────────────
function Toast({ toast }) {
  if (!toast) return null;
  const isError = toast.type === "error";
  return (
    <div style={{ position:"fixed", bottom:24, right:24, zIndex:2000,
      padding:"12px 18px", borderRadius:10, maxWidth:320,
      background: isError ? "#FFF3F3" : "#E1F5EE",
      border: `1px solid ${isError ? "#FECACA" : "#6FCFA7"}`,
      color: isError ? "#B91C1C" : "#1D9E75",
      boxShadow:"0 8px 24px rgba(0,0,0,0.12)",
      fontSize:13, fontWeight:500,
      display:"flex", alignItems:"center", gap:8,
      animation:"toastIn 0.2s ease" }}>
      <span>{isError ? "⚠️" : "✓"}</span>
      {toast.message}
    </div>
  );
}
const FIELD_MAPS = {
  brand:      [{ key:"BRANDNAME",label:"Product"},{ key:"BRAND_ABBREVATION",label:"Abbreviation"},{ key:"GENERIC",label:"Generic"},{ key:"CLASSNAME",label:"Class"},{ key:"SUBCLASSNAME",label:"Sub Class"},{ key:"MOANAME",label:"MoA"}],
  company:    [{ key:"SPONSORNAME",label:"Company Name"},{ key:"SPONSORABBREV",label:"Abbreviation"}],
  indication: [{ key:"INDICATIONNAME",label:"Indication"},{ key:"INDICATIONABBREV",label:"Abbreviation"}],
  phase:      [{ key:"PHASENAME",label:"Phase"},{ key:"SORTING",label:"Sort Order"},{ key:"CHART",label:"Show in Chart"}],
  tstatus:    [{ key:"TRIALSTATUSNAME",label:"Status"}],
  country:    [{ key:"COUNTRYNAME",label:"Country"},{ key:"REGIONNAME",label:"Region"}],
  region:     [{ key:"REGIONNAME",label:"Region"}],
  moa:        [{ key:"MOANAME",label:"MoA"},{ key:"MOAABBREV",label:"Abbreviation"}],
  generic:    [{ key:"GENERIC",label:"Generic Name"}],
  class:      [{ key:"CLASS",label:"Class Name"}],
  subclass:   [{ key:"SUBCLASS",label:"Sub Class"},{ key:"CLASS",label:"Class"},{ key:"DISPLAY",label:"Display"}],
  roa:        [{ key:"DELIVERY",label:"RoA"}],
};

// ─── Nav groups ───────────────────────────────────────────────────────────────
const NAV_GROUPS = [
  { group:"", items: [
    { key:"brand",      label:"Product",       endpoint:"get_brand_list",      idKey:"BRANDID",       nameKey:"BRANDNAME"       },
    { key:"company",    label:"Company",       endpoint:"get_company_list",    idKey:"SPONSORID",     nameKey:"SPONSORNAME"     },
    { key:"indication", label:"Indication",    endpoint:"get_indication_list", idKey:"INDICATIONID",  nameKey:"INDICATIONNAME"  },
    { key:"phase",      label:"Phase",         endpoint:"get_phase_list",      idKey:"PHASEID",       nameKey:"PHASENAME"       },
    { key:"tstatus",    label:"Trial Status",  endpoint:"get_tstatus_list",    idKey:"TRIALSTATUSID", nameKey:"TRIALSTATUSNAME" },
    { key:"country",    label:"Country",       endpoint:"get_country_list",    idKey:"COUNTRYID",     nameKey:"COUNTRYNAME"     },
  ]},
  { group:"Field Admin", items: [
    { key:"region",   label:"Region",          endpoint:"get_region_list",   idKey:"REGIONID",   nameKey:"REGIONNAME" },
    { key:"moa",      label:"MoA",             endpoint:"get_moa_list",      idKey:"MOAID",      nameKey:"MOANAME"    },
    { key:"generic",  label:"Generic",         endpoint:"get_generic_list",  idKey:"GENERICID",  nameKey:"GENERIC"    },
    { key:"class",    label:"Class",           endpoint:"get_class_list",    idKey:"CLASSID",    nameKey:"CLASS"      },
    { key:"subclass", label:"Sub-Class",       endpoint:"get_subclass_list", idKey:"SUBCLASSID", nameKey:"SUBCLASS"   },
    { key:"roa",      label:"RoA",             endpoint:"get_roa_list",      idKey:"DELIVERYID", nameKey:"DELIVERY"   },
  ]},
  { group:"News", items: [
    { key:"news", label:"News", special:"news" },
  ]},
];
const ALL_ITEMS = NAV_GROUPS.flatMap(g => g.items);

// ─── Shared styles ────────────────────────────────────────────────────────────
const inp = {
  width:"100%", boxSizing:"border-box", padding:"7px 10px",
  borderRadius:7, border:`1px solid ${C.border}`, fontSize:13,
  color:C.text, background:"#fff", outline:"none", fontFamily:"inherit",
};

// ─── Inline add row — always visible at bottom of dropdown/chips ─────────────
function InlineAddRow({ field, user, activeSub, onAdded }) {
  const [val,    setVal]    = useState("");
  const [saving, setSaving] = useState(false);
  const [done,   setDone]   = useState(false);

  const save = async () => {
    if (!val.trim()) return;
    setSaving(true);
    try {
      const res = await apiFetch(field.quickAdd.endpoint,
        { [field.quickAdd.paramName]: val.trim() }, user, activeSub);
      if (res.STATUS === "1" || res.STATUS === 1) {
        setDone(true); setVal("");
        setTimeout(() => setDone(false), 1200);
        onAdded();
      }
    } finally { setSaving(false); }
  };

  return (
    <div style={{ display:"flex", gap:6, padding:"6px 8px",
      borderTop:`1px solid ${C.border}`, background:"#F7F9FC", alignItems:"center" }}>
      <input value={val} onChange={e => setVal(e.target.value)}
        onKeyDown={e => e.key==="Enter" && save()}
        placeholder={`+ New ${field.quickAdd.label}…`}
        style={{ flex:1, padding:"4px 8px", borderRadius:6, border:`1px solid ${C.border}`,
          fontSize:12, outline:"none", fontFamily:"inherit", color:C.text, background:"#fff" }} />
      <button onClick={save} disabled={saving || !val.trim()}
        style={{ padding:"4px 10px", borderRadius:6, border:"none", fontSize:12,
          background: val.trim() ? C.primary : C.border,
          color:"#fff", cursor: val.trim() ? "pointer" : "default", fontWeight:600 }}>
        {done ? "✓" : saving ? "…" : "Add"}
      </button>
    </div>
  );
}

// ─── QuickAddForm — simple (just a name) or with extra fields (e.g. subclass+class) ──
function QuickAddForm({ field, drops, user, activeSub, onAdded }) {
  const { theme }   = useTheme();
  const primary     = theme?.primary || C.primary;
  const accentLight = theme?.accentLight || C.light;
  const qa          = field.quickAdd;
  const hasExtra    = qa.extraFields && qa.extraFields.length > 0;

  const [open,    setOpen]    = useState(false);
  const [val,     setVal]     = useState("");
  const [extras,  setExtras]  = useState({});
  const [saving,  setSaving]  = useState(false);

  const save = async () => {
    if (!val.trim()) return;
    setSaving(true);
    try {
      const body = { [qa.paramName]: val.trim(), ...extras };
      const res = await apiFetch(qa.endpoint, body, user, activeSub);
      if (res.STATUS === "1" || res.STATUS === 1) {
        const newId = extractNewId(res, field.idKey);
        setVal(""); setExtras({}); setOpen(false);
        await onAdded(newId);
      }
    } finally { setSaving(false); }
  };

  if (!hasExtra) {
    // simple — single input + ↵
    return (
      <div style={{ display:"flex", gap:5, alignItems:"center",
        background:"#F7F9FC", border:`1px solid ${C.border}`,
        borderRadius:7, padding:"0 6px", flexShrink:0, maxWidth:180 }}>
        <input value={val} onChange={e => setVal(e.target.value)}
          onKeyDown={e => e.key==="Enter" && save()}
          placeholder={`New ${qa.label}…`}
          style={{ border:"none", outline:"none", background:"transparent",
            fontSize:12, color:C.text, width:110, fontFamily:"inherit", padding:"6px 0" }} />
        <span style={{ fontSize:10, color:C.hint, whiteSpace:"nowrap" }}>↵ add</span>
      </div>
    );
  }

  // has extra fields — collapsed until user clicks
  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
        style={{ flexShrink:0, padding:"0 10px", height:36, borderRadius:7, cursor:"pointer",
          border:`1px solid ${C.border}`, background:"#F7F9FC",
          color:C.muted, fontSize:12, fontWeight:600, whiteSpace:"nowrap" }}>
        + New {qa.label}
      </button>
    );
  }

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:5, flexShrink:0, width:190,
      background:"#F7F9FC", border:`1px solid ${C.border}`, borderRadius:7, padding:"7px 8px" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <span style={{ fontSize:10, color:C.hint, fontWeight:700,
          textTransform:"uppercase", letterSpacing:"0.3px" }}>
          New {qa.label}
        </span>
        <button onClick={() => { setOpen(false); setVal(""); setExtras({}); }}
          style={{ background:"none", border:"none", cursor:"pointer",
            fontSize:14, color:C.hint, padding:0, lineHeight:1 }}>×</button>
      </div>

      <input autoFocus value={val} onChange={e => setVal(e.target.value)}
        onKeyDown={e => e.key==="Enter" && save()}
        placeholder={`${qa.label} name…`}
        style={{ border:`1px solid ${C.border}`, borderRadius:5, outline:"none",
          fontSize:12, color:C.text, padding:"4px 7px", fontFamily:"inherit", background:"#fff" }} />

      {qa.extraFields.map(ef => (
        <div key={ef.param}>
          <div style={{ fontSize:10, color:C.hint, marginBottom:3 }}>{ef.label}</div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>
            {(drops[ef.dropKey]||[]).map(opt => {
              const id  = opt[ef.idKey];
              const sel = String(extras[ef.param]) === String(id);
              return (
                <button key={id} onClick={() => setExtras(x => ({ ...x, [ef.param]: sel ? "" : id }))}
                  style={{ padding:"2px 8px", borderRadius:20, cursor:"pointer", fontSize:11,
                    fontWeight: sel ? 600 : 400, transition:"all 0.1s",
                    border:`1px solid ${sel ? primary : C.border}`,
                    background: sel ? accentLight : "#fff",
                    color: sel ? primary : C.muted }}>
                  {opt[ef.labelKey]}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      <button onClick={save} disabled={saving || !val.trim()}
        style={{ padding:"4px", borderRadius:5, border:"none", fontSize:12,
          background: val.trim() ? primary : C.border,
          color:"#fff", cursor: val.trim() ? "pointer" : "default", fontWeight:600 }}>
        {saving ? "…" : `Add ${qa.label}`}
      </button>
    </div>
  );
}

// ─── Smart dropdown — controlled open via parent ──────────────────────────────
function SmartDrop({ field, value, options, onChange, user, activeSub, onRefreshDrop, isOpen, onToggle, onClose }) {
  const { theme } = useTheme();
  const primary   = theme?.primary || C.primary;
  const [search,  setSearch] = useState("");
  const ref = useRef();

  useEffect(() => {
    if (!isOpen) return;
    const h = e => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", h, true);
    return () => document.removeEventListener("mousedown", h, true);
  }, [isOpen]);

  useEffect(() => { if (!isOpen) setSearch(""); }, [isOpen]);

  const filtered = options.filter(o => o[field.labelKey]?.toLowerCase().includes(search.toLowerCase()));
  const selected = options.find(o => String(o[field.idKey]) === String(value));

  return (
    <div ref={ref} style={{ position:"relative", flex:1 }}>
      <div onClick={onToggle}
        style={{ ...inp, display:"flex", alignItems:"center", justifyContent:"space-between",
          cursor:"pointer", userSelect:"none", background:"#fff" }}>
        <span style={{ color: selected ? C.text : C.hint, fontSize:13 }}>
          {selected ? selected[field.labelKey] : `— Select ${field.label} —`}
        </span>
        <span style={{ fontSize:10, color:C.hint, marginLeft:6,
          display:"inline-block", transition:"transform 0.15s",
          transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}>▾</span>
      </div>

      {isOpen && (
        <div style={{ position:"absolute", top:"calc(100% + 4px)", left:0, right:0,
          background:"#fff", border:`1px solid ${C.border}`, borderRadius:8,
          boxShadow:"0 8px 24px rgba(0,0,0,0.14)", zIndex:9999, overflow:"hidden" }}>
          <div style={{ padding:"6px 8px", borderBottom:`1px solid ${C.border}` }}>
            <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search…"
              style={{ ...inp, padding:"4px 8px", fontSize:12, background:"#F7F9FC" }} />
          </div>
          <div style={{ maxHeight:180, overflowY:"auto" }}>
            <div onClick={() => { onChange(""); onClose(); }}
              style={{ padding:"7px 10px", fontSize:13, color:C.hint, cursor:"pointer" }}>
              — None —
            </div>
            {filtered.map(o => {
              const isSel = String(o[field.idKey]) === String(value);
              return (
                <div key={o[field.idKey]}
                  onClick={() => { onChange(o[field.idKey]); onClose(); }}
                  style={{ padding:"7px 10px", fontSize:13, cursor:"pointer",
                    background: isSel ? (theme?.accentLight||C.light) : "transparent",
                    color: isSel ? primary : C.text, fontWeight: isSel ? 600 : 400 }}
                  onMouseEnter={e => { if(!isSel) e.currentTarget.style.background="#F7F9FC"; }}
                  onMouseLeave={e => { if(!isSel) e.currentTarget.style.background="transparent"; }}>
                  {o[field.labelKey]}
                </div>
              );
            })}
            {filtered.length === 0 && (
              <div style={{ padding:"10px", fontSize:12, color:C.hint, textAlign:"center" }}>No results</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Form modal ───────────────────────────────────────────────────────────────
function FormModal({ menuCfg, editItem, user, activeSub, onClose, onSaved, onToast }) {
  const { theme }   = useTheme();
  const primary     = theme?.primary    || C.primary;
  const accentLight = theme?.accentLight|| C.light;
  const cfg         = FORM_CFG[menuCfg.key];
  const isEdit      = !!editItem;

  // build initial values using explicit editKey mapping
  const buildValues = (item) => {
    if (!item) return {};
    const v = {};
    cfg.fields.forEach(f => {
      if (f.type === "multiDrop") {
        // ROA: pre-select ids from editItem.ROA array
        v[f.name] = (item.ROA || []).map(r => r.DELIVEYID ?? r.DELIVERYID);
      } else if (f.type === "drop") {
        v[f.name] = item[f.editKey] ?? "";
      } else if (f.type === "toggle") {
        v[f.name] = item[f.editKey] ?? 0;
      } else if (f.type === "file") {
        v[f.name] = null;
      } else {
        v[f.name] = item[f.editKey] ?? "";
      }
    });
    return v;
  };

  const [values,      setValues]      = useState(() => buildValues(editItem));
  const [drops,       setDrops]       = useState({});
  const [openDrop,    setOpenDrop]    = useState(null);
  const [saving,      setSaving]      = useState(false);
  // logo: start with existing logo url if editing
  const existingLogo = editItem?.BASE_URL
    ? editItem.BASE_URL + (editItem.COMPANY_LOGO || "")
    : editItem?.COMPANY_LOGO
      ? (LOGO_BASE || "") + editItem.COMPANY_LOGO
      : null;
  const [logoPreview, setLogoPreview] = useState(existingLogo);

  // load dropdowns
  const loadDrop = useCallback(async (dropKey) => {
    if (cfg.dropsEndpoint) {
      const res = await apiFetch(cfg.dropsEndpoint, {}, user, activeSub);
      if (res.STATUS === "1" || res.STATUS === 1) setDrops(res.DATA || {});
      return;
    }
    const field = cfg.fields.find(f2 => f2.dropKey === dropKey && f2.listEndpoint);
    if (!field) return;
    const res = await apiFetch(field.listEndpoint, {}, user, activeSub);
    if (res.STATUS === "1" || res.STATUS === 1)
      setDrops(prev => ({ ...prev, [dropKey]: res.DATA || [] }));
  }, [cfg, user, activeSub]);

  useEffect(() => {
    if (cfg.dropsEndpoint) { loadDrop(null); return; }
    cfg.fields.filter(f => f.listEndpoint).forEach(f => loadDrop(f.dropKey));
  }, [loadDrop]);

  const set         = (name, val) => setValues(v => ({ ...v, [name]: val }));
  const toggleMulti = (name, id)  => setValues(v => {
    const arr = (v[name] || []).map(String);
    const sid = String(id);
    return { ...v, [name]: arr.includes(sid) ? arr.filter(x => x!==sid) : [...arr, sid] };
  });
  const handleFile  = (name, file) => {
    set(name, file);
    setLogoPreview(file ? URL.createObjectURL(file) : existingLogo);
  };

  const handleSave = async () => {
    for (const f of cfg.fields) {
      if (f.required && !values[f.name]) {
        onToast({ type:"error", message:`${f.label} is required.` }); return;
      }
    }
    setSaving(true);
    try {
      // strip file fields — company logo is uploaded separately after save
      const fd = new FormData();
      fd.append("subprojectid", activeSub?.SUBPROJECTID || "");
      fd.append("userid",       user?.USERID || "");
      if (isEdit) fd.append(cfg.editIdParam, editItem[cfg.editIdKey]);

      cfg.fields.forEach(f => {
        if (f.type === "multiDrop") {
          (values[f.name] || []).forEach(id => fd.append(f.name + "[]", id));
        } else if (f.type === "file") {
          // skip — handled separately below
        } else {
          fd.append(f.name, values[f.name] ?? "");
        }
      });

      const res = await apiFetchFormData(cfg.addEndpoint, fd, user);
      if (res.STATUS === "1" || res.STATUS === 1) {
        let savedId = null;
        if (isEdit) {
          savedId = editItem[cfg.editIdKey];
        } else {
          const dataObj = res.DATA || {};
          if (Array.isArray(dataObj)) {
            // DATA is a direct array e.g. company
            savedId = dataObj[0]?.[cfg.editIdKey] ?? null;
          } else {
            // DATA is an object with named array e.g. { PRODUCT: [...] }
            const firstArr = Object.values(dataObj).find(v => Array.isArray(v) && v.length > 0);
            if (firstArr) savedId = firstArr[0][cfg.editIdKey] ?? null;
          }
        }

        // upload logo if provided (company only)
        const logoField = cfg.fields.find(f => f.type === "file");
        if (logoField && values[logoField.name] && savedId) {
          const logoFd = new FormData();
          logoFd.append("sponsorid", savedId);
          logoFd.append("files",     values[logoField.name]);
          await apiFetchFormData("upload_company_logo", logoFd, user);
        }

        onToast({ type:"success", message: isEdit ? "Updated successfully!" : "Added successfully!" });
        setTimeout(() => { onSaved(savedId); onClose(); }, 600);
      } else {
        onToast({ type:"error", message: res.MESSAGE || res.message || "Something went wrong." });
      }
    } catch { onToast({ type:"error", message:"Network error. Please try again." }); }
    finally  { setSaving(false); }
  };

  return (
    <>
      <div onMouseDown={onClose}
        style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", zIndex:1100 }} />

      <div onMouseDown={e => e.stopPropagation()}
        style={{ position:"fixed", top:"50%", left:"50%", transform:"translate(-50%,-50%)",
          width:"min(780px,75vw)", background:"#fff", borderRadius:12,
          boxShadow:"0 20px 60px rgba(0,0,0,0.22)", zIndex:1101,
          display:"flex", flexDirection:"column", maxHeight:"90vh", overflow:"hidden" }}>

        {/* header */}
        <div style={{ padding:"14px 18px", borderBottom:`1px solid ${C.border}`,
          display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div>
            <div style={{ fontSize:15, fontWeight:700, color:C.text }}>
              {isEdit ? `Edit ${SINGULAR[menuCfg.key]}` : `Add ${SINGULAR[menuCfg.key]}`}
            </div>
            {isEdit && (
              <div style={{ fontSize:11, color:C.hint, marginTop:1 }}>
                Editing: {editItem[ALL_ITEMS.find(m => m.key===menuCfg.key)?.nameKey] || ""}
              </div>
            )}
          </div>
          <button onClick={onClose}
            style={{ background:"none", border:"none", cursor:"pointer",
              fontSize:20, color:"#9CA3AF", lineHeight:1, padding:0 }}>×</button>
        </div>

        {/* body */}
        <div style={{ padding:"16px 18px", overflowY:"auto", flex:1, minHeight:0 }}>
          {cfg.fields.map(f => (
            <div key={f.name} style={{ marginBottom:14 }}>
              <label style={{ display:"block", fontSize:11, fontWeight:700, color:C.hint,
                textTransform:"uppercase", letterSpacing:"0.4px", marginBottom:5 }}>
                {f.label}{f.required && <span style={{ color:"#E53E3E", marginLeft:2 }}>*</span>}
              </label>

              {/* text / number */}
              {(f.type==="text" || f.type==="number") && (
                <input type={f.type} value={values[f.name]||""} onChange={e => set(f.name, e.target.value)}
                  style={inp} placeholder={`Enter ${f.label}…`} />
              )}

              {/* toggle */}
              {f.type==="toggle" && (
                <div style={{ display:"flex", gap:8 }}>
                  {[{v:1,l:"Yes"},{v:0,l:"No"}].map(opt => (
                    <button key={opt.v} onClick={() => set(f.name, opt.v)}
                      style={{ flex:1, padding:"6px", borderRadius:7, cursor:"pointer", fontSize:13,
                        border:`1px solid ${Number(values[f.name])===opt.v ? primary : C.border}`,
                        background: Number(values[f.name])===opt.v ? accentLight : "#F7F9FC",
                        color: Number(values[f.name])===opt.v ? primary : C.muted, fontWeight:600 }}>
                      {opt.l}
                    </button>
                  ))}
                </div>
              )}

              {/* file upload (company logo) */}
              {f.type==="file" && (
                <div>
                  <label style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer",
                    border:`1px dashed ${C.border}`, borderRadius:8, padding:"10px 12px",
                    background:"#F7F9FC" }}>
                    {logoPreview ? (
                      <img src={logoPreview} alt="logo"
                        style={{ width:40, height:40, objectFit:"contain", borderRadius:6,
                          border:`1px solid ${C.border}` }}
                        onError={e => e.target.style.display="none"} />
                    ) : (
                      <div style={{ width:40, height:40, borderRadius:6, background:C.light,
                        display:"flex", alignItems:"center", justifyContent:"center",
                        fontSize:18, color:primary }}>🏢</div>
                    )}
                    <div>
                      <div style={{ fontSize:13, fontWeight:500, color:C.text }}>
                        {values[f.name] ? values[f.name].name : (logoPreview ? "Change logo" : "Upload logo")}
                      </div>
                      <div style={{ fontSize:11, color:C.hint }}>PNG, JPG up to 2MB</div>
                    </div>
                    <input type="file" accept={f.accept||"image/*"} style={{ display:"none" }}
                      onChange={e => handleFile(f.name, e.target.files[0] || null)} />
                  </label>
                  {values[f.name] && (
                    <button onClick={() => handleFile(f.name, null)}
                      style={{ marginTop:4, fontSize:11, color:C.error, background:"none",
                        border:"none", cursor:"pointer", padding:0 }}>
                      ✕ Remove
                    </button>
                  )}
                </div>
              )}

              {/* smart single dropdown — parallel quick add */}
              {f.type==="drop" && (
                <div style={{ display:"flex", gap:8 }}>
                  <SmartDrop field={f} value={values[f.name]||""}
                    options={drops[f.dropKey]||[]}
                    onChange={val => set(f.name, val)}
                    user={user} activeSub={activeSub}
                    onRefreshDrop={key => loadDrop(key)}
                    isOpen={openDrop === f.name}
                    onToggle={() => setOpenDrop(o => o === f.name ? null : f.name)}
                    onClose={() => setOpenDrop(null)}
                  />
                  {f.quickAdd && (
                    <QuickAddForm field={f} drops={drops} user={user} activeSub={activeSub}
                      onAdded={async (newId) => {
                        await loadDrop(f.dropKey);
                        if (newId) set(f.name, newId);
                      }} />
                  )}
                </div>
              )}

              {/* multi-select chips — parallel quick add */}
              {f.type==="multiDrop" && (
                <div style={{ display:"flex", gap:8, alignItems:"flex-start" }}>
                  <div style={{ flex:1, border:`1px solid ${C.border}`, borderRadius:8,
                    padding:"8px", display:"flex", flexWrap:"wrap", gap:6, minHeight:40, background:"#fff" }}>
                    {(drops[f.dropKey]||[]).length === 0 && (
                      <span style={{ fontSize:12, color:C.hint }}>Loading…</span>
                    )}
                    {(drops[f.dropKey]||[]).map(opt => {
                      const id  = opt[f.idKey];
                      const sel = (values[f.name]||[]).map(String).includes(String(id));
                      return (
                        <button key={id} onClick={() => toggleMulti(f.name, id)}
                          style={{ padding:"4px 12px", borderRadius:20, cursor:"pointer", fontSize:12,
                            fontWeight: sel ? 600 : 400, transition:"all 0.12s",
                            border:`1px solid ${sel ? primary : C.border}`,
                            background: sel ? accentLight : "#F7F9FC",
                            color: sel ? primary : C.muted }}>
                          {opt[f.labelKey]}
                        </button>
                      );
                    })}
                  </div>
                  {f.quickAdd && (
                    <div style={{ display:"flex", flexDirection:"column", gap:4,
                      background:"#F7F9FC", border:`1px solid ${C.border}`,
                      borderRadius:7, padding:"6px 8px", flexShrink:0, width:160 }}>
                      <span style={{ fontSize:10, color:C.hint, fontWeight:700,
                        textTransform:"uppercase", letterSpacing:"0.3px" }}>
                        New {f.quickAdd.label}
                      </span>
                      <input
                        placeholder={`Type & press ↵`}
                        style={{ border:`1px solid ${C.border}`, borderRadius:5, outline:"none",
                          fontSize:12, color:C.text, padding:"4px 7px",
                          fontFamily:"inherit", background:"#fff" }}
                        onKeyDown={async e => {
                          if (e.key !== "Enter") return;
                          const v = e.target.value.trim();
                          if (!v) return;
                          const res = await apiFetch(f.quickAdd.endpoint,
                            { [f.quickAdd.paramName]: v }, user, activeSub);
                          if (res.STATUS === "1" || res.STATUS === 1) {
                            e.target.value = "";
                            const newId = extractNewId(res, f.idKey);
                            await loadDrop(f.dropKey);
                            if (newId) toggleMulti(f.name, newId);
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* single-select chips — for Region, Class */}
              {f.type==="singleChip" && (
                <div style={{ display:"flex", gap:8, alignItems:"flex-start" }}>
                  <div style={{ flex:1, border:`1px solid ${C.border}`, borderRadius:8,
                    padding:"8px", display:"flex", flexWrap:"wrap", gap:6, minHeight:40, background:"#fff" }}>
                    {(drops[f.dropKey]||[]).length === 0 && (
                      <span style={{ fontSize:12, color:C.hint }}>Loading…</span>
                    )}
                    {(drops[f.dropKey]||[]).map(opt => {
                      const id  = opt[f.idKey];
                      const sel = String(values[f.name]) === String(id);
                      return (
                        <button key={id}
                          onClick={() => set(f.name, sel ? "" : id)}
                          style={{ padding:"4px 12px", borderRadius:20, cursor:"pointer", fontSize:12,
                            fontWeight: sel ? 600 : 400, transition:"all 0.12s",
                            border:`1px solid ${sel ? primary : C.border}`,
                            background: sel ? accentLight : "#F7F9FC",
                            color: sel ? primary : C.muted }}>
                          {opt[f.labelKey]}
                        </button>
                      );
                    })}
                  </div>
                  {f.quickAdd && (
                    <div style={{ display:"flex", flexDirection:"column", gap:4,
                      background:"#F7F9FC", border:`1px solid ${C.border}`,
                      borderRadius:7, padding:"6px 8px", flexShrink:0, width:160 }}>
                      <span style={{ fontSize:10, color:C.hint, fontWeight:700,
                        textTransform:"uppercase", letterSpacing:"0.3px" }}>
                        New {f.quickAdd.label}
                      </span>
                      <input placeholder="Type & press ↵"
                        style={{ border:`1px solid ${C.border}`, borderRadius:5, outline:"none",
                          fontSize:12, color:C.text, padding:"4px 7px",
                          fontFamily:"inherit", background:"#fff" }}
                        onKeyDown={async e => {
                          if (e.key !== "Enter") return;
                          const v = e.target.value.trim();
                          if (!v) return;
                          const res = await apiFetch(f.quickAdd.endpoint,
                            { [f.quickAdd.paramName]: v }, user, activeSub);
                          if (res.STATUS === "1" || res.STATUS === 1) {
                            e.target.value = "";
                            const newId = extractNewId(res, f.idKey);
                            await loadDrop(f.dropKey);
                            if (newId) set(f.name, newId);
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
              )}

            </div>
          ))}

        </div>

        {/* footer */}
        <div style={{ padding:"12px 18px", borderTop:`1px solid ${C.border}`,
          display:"flex", gap:8, justifyContent:"flex-end" }}>
          <button onClick={onClose}
            style={{ padding:"7px 18px", borderRadius:7, border:`1px solid ${C.border}`,
              background:"#F7F9FC", color:C.muted, fontSize:13, cursor:"pointer", fontWeight:500 }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            style={{ padding:"7px 22px", borderRadius:7, border:"none",
              background: saving ? "#aaa" : primary,
              color:"#fff", fontSize:13, fontWeight:600, minWidth:90,
              cursor: saving ? "wait" : "pointer",
              display:"flex", alignItems:"center", gap:6, justifyContent:"center" }}>
            {saving
              ? <><div style={{ width:13,height:13,border:"2px solid rgba(255,255,255,0.4)",
                  borderTop:"2px solid #fff",borderRadius:"50%",animation:"acSpin 0.7s linear infinite"}} />
                  Saving…</>
              : "Save"}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── List panel — modern list view ───────────────────────────────────────────
function ListPanel({ menuCfg, user, activeSub, refreshKey, savedId, onAdd, onEdit }) {
  const { theme }   = useTheme();
  const primary     = theme?.primary     || C.primary;
  const accentLight = theme?.accentLight || C.light;

  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [search,  setSearch]  = useState("");
  const listRef   = useRef();

  const fields = FIELD_MAPS[menuCfg.key] || [{ key:menuCfg.nameKey, label:menuCfg.label }];

  const load = useCallback(() => {
    setLoading(true); setError(null);
    apiFetch(menuCfg.endpoint, {}, user, activeSub)
      .then(res => {
        if (res.STATUS==="1"||res.STATUS===1) setData(res.DATA||[]);
        else setError("No data returned.");
      })
      .catch(() => setError("Failed to load."))
      .finally(() => setLoading(false));
  }, [menuCfg.endpoint, refreshKey]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!savedId||!listRef.current) return;
    const el = listRef.current.querySelector(`[data-id="${savedId}"]`);
    if (el) el.scrollIntoView({ block:"center", behavior:"smooth" });
  }, [savedId, data]);

  const filtered = data.filter(item => {
    const v = item[menuCfg.nameKey];
    return !v || v.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", background:"#F7F9FC" }}>

      {/* toolbar */}
      <div style={{ display:"flex", alignItems:"center", gap:12,
        padding:"12px 20px", background:"#fff",
        borderBottom:`1px solid ${C.border}`, flexShrink:0 }}>
        <div style={{ flex:1, position:"relative" }}>
          <span style={{ position:"absolute", left:10, top:"50%",
            transform:"translateY(-50%)", fontSize:12,
            color:C.hint, pointerEvents:"none" }}>🔍</span>
          <input value={search} onChange={e=>setSearch(e.target.value)}
            placeholder={`Search ${menuCfg.label}…`}
            style={{ width:"100%", boxSizing:"border-box",
              padding:"7px 10px 7px 30px", borderRadius:8,
              border:`1px solid ${C.border}`, fontSize:13,
              outline:"none", fontFamily:"inherit",
              color:C.text, background:"#F7F9FC" }} />
        </div>
        {!loading && (
          <span style={{ fontSize:12, color:C.hint, whiteSpace:"nowrap" }}>
            {filtered.length} {menuCfg.label}
          </span>
        )}
        <button onClick={onAdd}
          style={{ padding:"7px 18px", borderRadius:8, border:"none",
            background:primary, color:"#fff", fontSize:13, fontWeight:600,
            cursor:"pointer", flexShrink:0,
            boxShadow:`0 2px 10px ${primary}55` }}>
          + Add
        </button>
      </div>

      {/* list */}
      <div ref={listRef} style={{ flex:1, overflowY:"auto", padding:"12px 20px" }}>
        {loading && (
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center",
            paddingTop:80, gap:12, color:C.hint }}>
            <div style={{ width:28, height:28, border:`3px solid ${C.border}`,
              borderTop:`3px solid ${primary}`, borderRadius:"50%",
              animation:"acSpin 0.7s linear infinite" }} />
            <span style={{ fontSize:13 }}>Loading…</span>
          </div>
        )}
        {!loading && error && (
          <div style={{ padding:"14px 18px", borderRadius:10, background:"#FFF3F3",
            border:"1px solid #FECACA", color:"#B91C1C", fontSize:13 }}>{error}</div>
        )}
        {!loading && !error && filtered.length===0 && (
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center",
            paddingTop:80, gap:8, color:C.hint }}>
            <span style={{ fontSize:36 }}>🔍</span>
            <span style={{ fontSize:13 }}>Nothing found</span>
          </div>
        )}
        {!loading && !error && filtered.length>0 && (
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            {filtered.map(item => (
              <ListItem key={item[menuCfg.idKey]}
                item={item} menuCfg={menuCfg} fields={fields}
                isSaved={savedId && String(item[menuCfg.idKey])===String(savedId)}
                primary={primary} accentLight={accentLight}
                onEdit={onEdit} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── List item row ────────────────────────────────────────────────────────────
function ListItem({ item, menuCfg, fields, isSaved, primary, accentLight, onEdit }) {
  const [hovered, setHovered] = useState(false);

  const logoFile = item.COMPANY_LOGO || item.DESCRIPTION || "";
  const logoUrl  = logoFile ? (item.BASE_URL||"") + logoFile : null;
  const name     = item[fields[0]?.key] || "—";
  const initials = name.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();

  const abbrev   = item.BRAND_ABBREVATION || item.SPONSORABBREV ||
                   item.INDICATIONABBREV  || item.MOAABBREV || null;

  // secondary meta — pick meaningful ones per type
  const meta = [];
  if (menuCfg.key==="brand") {
    if (item.MOANAME)     meta.push({ label:"MoA",     val:item.MOANAME     });
    if (item.CLASSNAME)   meta.push({ label:"Class",   val:item.CLASSNAME   });
    if (item.GENERIC)     meta.push({ label:"Generic", val:item.GENERIC     });
  }
  if (menuCfg.key==="company"   && item.SPONSORABBREV) meta.push({ label:"Abbrev", val:item.SPONSORABBREV });
  if (menuCfg.key==="indication"&& item.INDICATIONABBREV) meta.push({ label:"Abbrev", val:item.INDICATIONABBREV });
  if (menuCfg.key==="country"   && item.REGIONNAME) meta.push({ label:"Region", val:item.REGIONNAME });
  if (menuCfg.key==="subclass"  && item.CLASS)      meta.push({ label:"Class",  val:item.CLASS      });
  if (menuCfg.key==="moa"       && item.MOAABBREV)  meta.push({ label:"Abbrev", val:item.MOAABBREV  });
  if (menuCfg.key==="phase") {
    if (item.SORTING!=null) meta.push({ label:"Order", val:`#${item.SORTING}` });
    meta.push({ label:"In Chart", val: Number(item.CHART)===1 ? "Yes":"No" });
  }

  return (
    <div data-id={item[menuCfg.idKey]}
      onMouseEnter={()=>setHovered(true)}
      onMouseLeave={()=>setHovered(false)}
      style={{ display:"flex", alignItems:"center", gap:12,
        padding:"10px 14px", borderRadius:10, background:"#fff",
        border:`1.5px solid ${isSaved ? primary : hovered ? primary+"50" : C.border}`,
        boxShadow: isSaved ? `0 0 0 3px ${primary}18` : hovered ? "0 2px 12px rgba(0,0,0,0.08)" : "none",
        transition:"all 0.15s", position:"relative" }}>

      {/* avatar */}
      {logoUrl
        ? <img src={logoUrl} alt="" onError={e=>{e.target.style.display="none";}}
            style={{ width:38, height:38, borderRadius:8, objectFit:"contain",
              background:"#F7F9FC", flexShrink:0 }} />
        : <div style={{ width:38, height:38, borderRadius:8, flexShrink:0,
            background:accentLight, display:"flex", alignItems:"center",
            justifyContent:"center", fontSize:13, fontWeight:800, color:primary }}>
            {initials}
          </div>
      }

      {/* main content */}
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
          <span style={{ fontSize:14, fontWeight:600, color:C.text }}>
            {name}
          </span>
          {abbrev && (
            <span style={{ fontSize:10, fontWeight:700, background:accentLight,
              color:primary, borderRadius:5, padding:"1px 7px" }}>
              {abbrev}
            </span>
          )}
        </div>
        {/* meta pills */}
        {meta.length > 0 && (
          <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginTop:4 }}>
            {meta.map((m,i) => (
              <span key={i} style={{ fontSize:11, color:C.muted }}>
                <span style={{ color:C.hint, marginRight:2 }}>{m.label}:</span>
                {m.val}
              </span>
            ))}
          </div>
        )}
        {/* ROA chips for brand */}
        {menuCfg.key==="brand" && item.ROA?.length>0 && (
          <div style={{ display:"flex", flexWrap:"wrap", gap:4, marginTop:5 }}>
            {item.ROA.map(r=>(
              <span key={r.DELIVEYID||r.DELIVERYID}
                style={{ fontSize:10, background:accentLight, color:primary,
                  borderRadius:20, padding:"1px 8px", fontWeight:500 }}>
                {r.DELIVERY}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* edit — ghost pencil on hover */}
      {hovered && (
        <button onClick={()=>onEdit(item)}
          style={{ width:30, height:30, borderRadius:8, border:"none",
            background:primary+"18", color:primary, fontSize:14,
            cursor:"pointer", flexShrink:0, display:"flex",
            alignItems:"center", justifyContent:"center" }}
          onMouseEnter={e=>e.currentTarget.style.background=primary+"30"}
          onMouseLeave={e=>e.currentTarget.style.background=primary+"18"}>
          ✏️
        </button>
      )}
    </div>
  );
}
// ─── Main AdminCorner ─────────────────────────────────────────────────────────
// ─── News Panel ───────────────────────────────────────────────────────────────
function NewsPanel({ user, activeSub }) {
  const [news,    setNews]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg,     setMsg]     = useState("");

  useEffect(()=>{
    setLoading(true); setMsg("");
    fetch(`${API_BASE}/scrape_news?userid=${user.USERID}&subprojectid=${activeSub?.SUBPROJECTID||""}`,
      {headers:{"Auth-Code":user.AUTH_CODE}})
      .then(r=>r.json())
      .then(res=>{
        if(res.STATUS==="1"||res.STATUS===1){
          setNews(Array.isArray(res.DATA)?res.DATA:[]);
          setMsg(res.MESSAGE||"");
        } else {
          setMsg(res.MESSAGE||"Failed to load news.");
        }
      })
      .catch(()=>setMsg("Network error."))
      .finally(()=>setLoading(false));
  },[]);

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
      <div style={{flexShrink:0,padding:"12px 20px",borderBottom:`1px solid ${C.border}`,
        display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div style={{fontSize:14,fontWeight:600,color:C.text}}>Today's News</div>
        {msg&&<div style={{fontSize:11,color:C.muted}}>{msg}</div>}
      </div>
      <div style={{flex:1,overflowY:"auto"}}>
        {loading&&(
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",
            padding:40,color:C.muted,fontSize:13,gap:8}}>
            <div style={{width:14,height:14,border:`2px solid ${C.border}`,
              borderTopColor:C.primary,borderRadius:"50%",animation:"acSpin .8s linear infinite"}}/>
            Loading…
          </div>
        )}
        {!loading&&!news.length&&(
          <div style={{textAlign:"center",padding:40,color:C.muted,fontSize:13}}>
            No news found for today.
          </div>
        )}
        {!loading&&news.map((item,i)=>(
          <div key={i} style={{padding:"12px 20px",borderBottom:`1px solid ${C.border}`}}>
            <div style={{fontSize:11,color:C.hint,marginBottom:3}}>{item.NEWS_DATE}</div>
            <div style={{fontSize:13,color:C.text,lineHeight:1.5,fontWeight:500,marginBottom:4}}>
              {item.TITLE}
            </div>
            {item.WEB_URL&&(
              <a href={item.WEB_URL} target="_blank" rel="noopener noreferrer"
                style={{fontSize:11,color:C.primary,textDecoration:"none",
                  display:"inline-flex",alignItems:"center",gap:4}}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
                  <polyline points="15 3 21 3 21 9"/>
                  <line x1="10" y1="14" x2="21" y2="3"/>
                </svg>
                Read more
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminCorner({ user, activeSub, onNavigate }) {
  const { theme }   = useTheme();
  const primary     = theme?.primary     || C.primary;
  const accentLight = theme?.accentLight || C.light;

  const [activeKey,  setActiveKey]  = useState("brand");
  const [modal,      setModal]      = useState(null);
  const [editItem,   setEditItem]   = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [toast,      setToast]      = useState(null);
  const [savedId,    setSavedId]    = useState(null);

  const mountedSubId = useRef(activeSub?.SUBPROJECTID);
  useEffect(() => {
    // if subproject changes or role drops, navigate away
    if (activeSub?.SUBPROJECTID !== mountedSubId.current || activeSub?.ROLE !== 1) {
      onNavigate?.("ataglance");
    }
  }, [activeSub?.SUBPROJECTID, activeSub?.ROLE]);

  const showToast   = (t) => { setToast(t); setTimeout(() => setToast(null), 3000); };
  const handleSaved = (id) => { setSavedId(id); setRefreshKey(k => k + 1); };
  const activeMenu  = ALL_ITEMS.find(m => m.key === activeKey);
  const handleMenuChange = (key) => { setActiveKey(key); setSavedId(null); };

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", overflow:"hidden", background:C.bg }}>

      {/* tab strip */}
      <div style={{ display:"flex", alignItems:"center", gap:2, padding:"0 20px",
        background:"#fff", borderBottom:`1px solid ${C.border}`,
        flexWrap:"wrap", flexShrink:0 }}>
        {NAV_GROUPS.filter(g=>g.group!=="News").map((grp, gi) => (
          <React.Fragment key={gi}>
            {grp.group && (
              <div style={{ width:1, height:18, background:C.border, margin:"0 6px", flexShrink:0 }} />
            )}
            {grp.items.map(m => (
              <button key={m.key} onClick={() => handleMenuChange(m.key)}
                style={{ padding:"12px 14px", border:"none", cursor:"pointer",
                  background:"transparent", fontSize:13, whiteSpace:"nowrap",
                  fontWeight: activeKey===m.key ? 600 : 400,
                  color: activeKey===m.key ? primary : C.muted,
                  borderBottom: activeKey===m.key ? `2px solid ${primary}` : "2px solid transparent",
                  transition:"all 0.12s", flexShrink:0 }}
                onMouseEnter={e=>{ if(activeKey!==m.key) e.currentTarget.style.color=C.text; }}
                onMouseLeave={e=>{ if(activeKey!==m.key) e.currentTarget.style.color=C.muted; }}>
                {m.label}
              </button>
            ))}
          </React.Fragment>
        ))}
        {/* News tab pinned to right */}
        <div style={{flex:1}}/>
        <div style={{ width:1, height:18, background:C.border, margin:"0 6px", flexShrink:0 }} />
        <button onClick={() => handleMenuChange("news")}
          style={{ padding:"12px 14px", border:"none", cursor:"pointer",
            background:"transparent", fontSize:13, whiteSpace:"nowrap",
            fontWeight: activeKey==="news" ? 600 : 400,
            color: activeKey==="news" ? primary : C.muted,
            borderBottom: activeKey==="news" ? `2px solid ${primary}` : "2px solid transparent",
            transition:"all 0.12s", flexShrink:0 }}
          onMouseEnter={e=>{ if(activeKey!=="news") e.currentTarget.style.color=C.text; }}
          onMouseLeave={e=>{ if(activeKey!=="news") e.currentTarget.style.color=C.muted; }}>
          📰 News
        </button>
      </div>

      {/* list */}
      <div style={{ flex:1, overflow:"hidden" }}>
        {activeKey==="news" ? (
          <NewsPanel user={user} activeSub={activeSub} />
        ) : activeMenu && (
          <ListPanel key={activeKey} menuCfg={activeMenu} user={user} activeSub={activeSub}
            refreshKey={refreshKey} savedId={savedId}
            onAdd={() => { setEditItem(null); setModal("add"); }}
            onEdit={(item) => { setEditItem(item); setModal("edit"); }} />
        )}
      </div>

      {modal && activeMenu && (
        <FormModal menuCfg={activeMenu} editItem={modal==="edit" ? editItem : null}
          user={user} activeSub={activeSub}
          onClose={() => { setModal(null); setEditItem(null); }}
          onSaved={handleSaved} onToast={showToast} />
      )}

      <Toast toast={toast} />

      <style>{`
        @keyframes acSpin  { to { transform: rotate(360deg); } }
        @keyframes toastIn { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
      `}</style>
    </div>
  );
}